import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CalendarClock, CheckCircle2, Search, XCircle } from 'lucide-react';

import { DataTable } from '@/components/shared/DataTable';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';
import { FilterBar } from '@/components/shared/FilterBar';
import { KpiCard } from '@/components/shared/KpiCard';
import { complianceMonitorService } from '@/services/complianceMonitor/complianceMonitor.service';
import type {
  ActionHorizon,
  ComplianceKpis,
  OrderPlanningSummary,
  SoDeliverySimulationRow,
} from '@/services/complianceMonitor/complianceMonitor.types';
import { ActionHorizonPill } from '../components/ActionHorizonPill';
import { BottlenecksPanel } from '../components/BottlenecksPanel';
import { SoDeliverySimulationSidebar } from '../components/SoDeliverySimulationSidebar';

function toIsoWeekLabel(dateStr: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  const utcDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = utcDate.getUTCDay() || 7;
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((utcDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${utcDate.getUTCFullYear()}-S${String(weekNo).padStart(2, '0')}`;
}

export function ComplianceMonitorPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<SoDeliverySimulationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [orderPlanningSummary, setOrderPlanningSummary] = useState<OrderPlanningSummary | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedSalesOrderId, setSelectedSalesOrderId] = useState<number | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOrder, setSearchOrder] = useState('');
  const [debouncedSearchOrder, setDebouncedSearchOrder] = useState('');
  const [selectedOrderStatus, setSelectedOrderStatus] = useState<string | null>(null);
  const [selectedSimulationStatus, setSelectedSimulationStatus] = useState<string | null>(null);
  const [selectedActionHorizon, setSelectedActionHorizon] = useState<ActionHorizon | null>(null);
  const [kpis, setKpis] = useState<ComplianceKpis | null>(null);
  const [kpisLoading, setKpisLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchKpis = async () => {
    setKpisLoading(true);
    try {
      const res = await complianceMonitorService.getKpis();
      setKpis(res.kpis);
    } catch (err) {
      console.error('[ComplianceMonitor] kpis error:', err);
      setKpis(null);
    } finally {
      setKpisLoading(false);
    }
  };

  const fetchPage = async (
    nextPage: number,
    options?: {
      searchOrder?: string;
      orderStatus?: string | null;
      simulationStatus?: string | null;
      actionHorizon?: ActionHorizon | null;
    },
    pageSizeOverride?: number,
  ) => {
    setLoading(true);
    const effectivePageSize = pageSizeOverride ?? pageSize;
    try {
      const horizon = options?.actionHorizon ?? selectedActionHorizon;
      const res = await complianceMonitorService.listSoDeliverySimulations({
        page: nextPage,
        page_size: effectivePageSize,
        include_reviewed: false,
        search_order: options?.searchOrder ?? debouncedSearchOrder,
        order_status: options?.orderStatus ?? selectedOrderStatus ?? undefined,
        simulation_status:
          (options?.simulationStatus ?? selectedSimulationStatus) === 'in_line' ||
          (options?.simulationStatus ?? selectedSimulationStatus) === 'out_of_plan'
            ? ((options?.simulationStatus ?? selectedSimulationStatus) as 'in_line' | 'out_of_plan')
            : undefined,
        action_horizon: horizon ? [horizon] : undefined,
      });
      setRows(res.results);
      setOrderPlanningSummary(res.order_planning_summary ?? null);
      setPage(res.pagination.current_page);
      setTotalPages(res.pagination.total_pages || 1);
      setTotalCount(res.pagination.total_count || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchKpis();
  }, [refreshKey]);

  useEffect(() => {
    fetchPage(1, {
      searchOrder: debouncedSearchOrder,
      orderStatus: selectedOrderStatus,
      simulationStatus: selectedSimulationStatus,
      actionHorizon: selectedActionHorizon,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearchOrder,
    selectedOrderStatus,
    selectedSimulationStatus,
    selectedActionHorizon,
    refreshKey,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchOrder(searchOrder.trim());
    }, 250);
    return () => clearTimeout(timer);
  }, [searchOrder]);

  const filterOptions = useMemo(
    () => [
      {
        id: 'action_horizon',
        name: t('complianceMonitor.filters.actionHorizon'),
        options: [
          { value: 'URGENT_THIS_WEEK', label: t('complianceMonitor.actionHorizon.URGENT_THIS_WEEK') },
          { value: 'SCHEDULE_NEXT_WEEK', label: t('complianceMonitor.actionHorizon.SCHEDULE_NEXT_WEEK') },
          { value: 'NOT_FEASIBLE', label: t('complianceMonitor.actionHorizon.NOT_FEASIBLE') },
          { value: 'OK', label: t('complianceMonitor.actionHorizon.OK') },
        ],
      },
      {
        id: 'order_status',
        name: t('complianceMonitor.filters.orderStatus'),
        options: [
          { value: 'NON_PROGRAMMATO', label: t('complianceMonitor.orderStatus.NON_PROGRAMMATO') },
          { value: 'PROGRAMMATO', label: t('complianceMonitor.orderStatus.PROGRAMMATO') },
        ],
      },
      {
        id: 'simulation_status',
        name: t('complianceMonitor.filters.status'),
        options: [
          { value: 'in_line', label: t('complianceMonitor.status.feasible') },
          { value: 'out_of_plan', label: t('complianceMonitor.status.notFeasible') },
        ],
      },
    ],
    [t]
  );

  const activeFilters = useMemo(() => {
    const active: Array<{ id: string; label: string; value: string }> = [];
    if (selectedActionHorizon) {
      const option = filterOptions[0].options.find((o) => String(o.value) === selectedActionHorizon);
      if (option) active.push({ id: 'action_horizon', label: option.label, value: selectedActionHorizon });
    }
    if (selectedOrderStatus) {
      const option = filterOptions[1].options.find((o) => String(o.value) === selectedOrderStatus);
      if (option) active.push({ id: 'order_status', label: option.label, value: selectedOrderStatus });
    }
    if (selectedSimulationStatus) {
      const option = filterOptions[2].options.find((o) => String(o.value) === selectedSimulationStatus);
      if (option) active.push({ id: 'simulation_status', label: option.label, value: selectedSimulationStatus });
    }
    return active;
  }, [filterOptions, selectedActionHorizon, selectedOrderStatus, selectedSimulationStatus]);

  const handleFilterChange = (nextFilters: Record<string, string[]>) => {
    if (Object.prototype.hasOwnProperty.call(nextFilters, 'action_horizon')) {
      const value = nextFilters.action_horizon?.[0];
      setSelectedActionHorizon((value as ActionHorizon | undefined) ?? null);
    }
    if (Object.prototype.hasOwnProperty.call(nextFilters, 'order_status')) {
      setSelectedOrderStatus(nextFilters.order_status?.[0] || null);
    }
    if (Object.prototype.hasOwnProperty.call(nextFilters, 'simulation_status')) {
      setSelectedSimulationStatus(nextFilters.simulation_status?.[0] || null);
    }
  };

  const clearSingleFilter = (filterId: string) => {
    if (filterId === 'action_horizon') setSelectedActionHorizon(null);
    if (filterId === 'order_status') setSelectedOrderStatus(null);
    if (filterId === 'simulation_status') setSelectedSimulationStatus(null);
  };

  const columns = useMemo(
    () => [
      {
        header: t('complianceMonitor.table.orderNumber'),
        accessor: 'order_number',
        sortable: false,
        cell: (v: string) => <span className="font-mono text-gray-900">{v}</span>,
      },
      {
        header: t('complianceMonitor.table.orderStatus'),
        accessor: 'sales_order_status',
        cell: (v: string) => {
          const rawValue = (v || '').toUpperCase().trim();
          const value = rawValue.replace(/\s+/g, '_');
          const colorClass =
            value === 'CONFIRMED' || value === 'PROGRAMMATO'
              ? 'bg-blue-50 text-blue-700 border-blue-200'
              : value === 'PENDING' || value === 'NON_PROGRAMMATO'
                ? 'bg-amber-50 text-amber-700 border-amber-200'
                : value === 'DELIVERED'
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : value === 'CANCELLED'
                    ? 'bg-red-50 text-red-700 border-red-200'
                    : 'bg-gray-50 text-gray-700 border-gray-200';

          const mappedValue =
            value === 'PENDING'
              ? 'NON_PROGRAMMATO'
              : value === 'CONFIRMED'
                ? 'PROGRAMMATO'
                : value;

          const labelKey = `complianceMonitor.orderStatus.${mappedValue}`;
          const translated = t(labelKey);
          const label = translated === labelKey ? (mappedValue || '—') : translated;

          return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border whitespace-nowrap ${colorClass}`}>
              {label}
            </span>
          );
        },
      },
      {
        header: t('complianceMonitor.table.orderDate'),
        accessor: 'order_date',
        cell: (v: string | null) => <span className="text-gray-900 font-medium">{toIsoWeekLabel(v)}</span>,
      },
      {
        header: t('complianceMonitor.table.articles'),
        accessor: 'items_count',
        cell: (v: number, item: SoDeliverySimulationRow) => (
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-gray-900 font-medium">{v ?? 0}</span>
            <span className="text-[10px] text-green-700 whitespace-nowrap">✓ {item.feasible_items_count ?? 0}</span>
            <span className="text-[10px] text-red-700 whitespace-nowrap">✗ {item.not_feasible_items_count ?? 0}</span>
          </div>
        ),
      },
      {
        header: t('complianceMonitor.table.expectedDelivery'),
        accessor: 'expected_delivery_date',
        cell: (v: string | null) => <span className="text-gray-900 font-medium">{toIsoWeekLabel(v)}</span>,
      },
      {
        header: t('complianceMonitor.table.nextPossibleDelivery'),
        accessor: 'earliest_feasible_delivery_week_bucket',
        cell: (v: string | null, item: SoDeliverySimulationRow) => {
          if (item.feasible) {
            return <span className="text-gray-400">—</span>;
          }
          return <span className="text-gray-900 font-medium">{toIsoWeekLabel(v)}</span>;
        },
      },
      {
        header: t('complianceMonitor.table.actionHorizon'),
        accessor: 'action_horizon',
        cell: (_value: unknown, item: SoDeliverySimulationRow) => (
          <ActionHorizonPill horizon={item.action_horizon} />
        ),
      },
    ],
    [t]
  );

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          title={t('complianceMonitor.kpis.urgentThisWeek')}
          value={kpisLoading || !kpis ? '—' : kpis.orders_urgent_this_week}
          icon={<AlertTriangle className="w-5 h-5" />}
          isCount
          onClick={() => setSelectedActionHorizon('URGENT_THIS_WEEK')}
        />
        <KpiCard
          title={t('complianceMonitor.kpis.scheduleNextWeek')}
          value={kpisLoading || !kpis ? '—' : kpis.orders_schedule_next_week}
          icon={<CalendarClock className="w-5 h-5" />}
          isCount
          onClick={() => setSelectedActionHorizon('SCHEDULE_NEXT_WEEK')}
        />
        <KpiCard
          title={t('complianceMonitor.kpis.notFeasible')}
          value={kpisLoading || !kpis ? '—' : kpis.orders_not_feasible}
          icon={<XCircle className="w-5 h-5" />}
          isCount
          onClick={() => setSelectedActionHorizon('NOT_FEASIBLE')}
        />
        <KpiCard
          title={t('complianceMonitor.kpis.ok')}
          value={kpisLoading || !kpis ? '—' : kpis.orders_ok}
          icon={<CheckCircle2 className="w-5 h-5" />}
          isCount
          onClick={() => setSelectedActionHorizon('OK')}
        />
      </div>

      <BottlenecksPanel limit={10} refreshKey={refreshKey} />

      <FilterBar
        filters={filterOptions}
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
        onClearFilter={clearSingleFilter}
        onClearAllFilters={() => {
          setSelectedActionHorizon(null);
          setSelectedOrderStatus(null);
          setSelectedSimulationStatus(null);
        }}
      />
      {orderPlanningSummary && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('complianceMonitor.summary.title')}</h3>
          <div className="overflow-x-auto">
            <table className="min-w-[420px] w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-medium text-gray-700">{t('complianceMonitor.summary.scopeLabel')}</th>
                  <th className="text-right py-2 px-2 font-medium text-gray-700 whitespace-nowrap">
                    {t('complianceMonitor.status.feasible')}
                  </th>
                  <th className="text-right py-2 px-2 font-medium text-gray-700 whitespace-nowrap">
                    {t('complianceMonitor.status.notFeasible')}
                  </th>
                  <th className="text-right py-2 pl-2 font-medium text-gray-700 whitespace-nowrap">
                    {t('complianceMonitor.summary.rowTotal')}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-2 pr-4 text-gray-900">{t('complianceMonitor.summary.rowDescription')}</td>
                  <td className="text-right py-2 px-2 tabular-nums text-gray-900">
                    {orderPlanningSummary.in_line_count}
                  </td>
                  <td className="text-right py-2 px-2 tabular-nums text-gray-900">
                    {orderPlanningSummary.out_of_plan_count}
                  </td>
                  <td className="text-right py-2 pl-2 tabular-nums font-medium text-gray-900">
                    {orderPlanningSummary.grand_total}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between">
          <h2 className="text-base font-semibold text-gray-900">{t('complianceMonitor.table.title')}</h2>
          <div className="w-full md:w-[320px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchOrder}
              onChange={(e) => setSearchOrder(e.target.value)}
              placeholder={t('complianceMonitor.filters.searchOrder')}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-[#1681ba] focus:border-[#1681ba]"
            />
          </div>
        </div>

        <div className="flex justify-end items-center mb-4">
          <div className="text-sm text-gray-500">
            {totalCount} {t('complianceMonitor.table.total')}
          </div>
        </div>

        <div className="relative">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center z-10 rounded-lg">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1681ba]" />
            </div>
          )}
          <DataTable
            columns={columns}
            data={rows}
            compact
            externalPagination={{
              currentPage: page,
              totalPages,
              totalCount,
              pageSize,
              onPageChange: (p) =>
                fetchPage(p, {
                  searchOrder: debouncedSearchOrder,
                  orderStatus: selectedOrderStatus,
                  simulationStatus: selectedSimulationStatus,
                }),
              onPageSizeChange: (size) => {
                setPageSize(size);
                setPage(1);
                void fetchPage(1, {
                  searchOrder: debouncedSearchOrder,
                  orderStatus: selectedOrderStatus,
                  simulationStatus: selectedSimulationStatus,
                }, size);
              },
            }}
            pagination
            onRowClick={(item: SoDeliverySimulationRow) => {
              setSelectedId(item.id);
              setSelectedSalesOrderId(item.sales_order_id);
              setSidebarOpen(true);
            }}
          />
        </div>
      </div>

      <SoDeliverySimulationSidebar
        simulationId={selectedId}
        salesOrderId={selectedSalesOrderId}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onReviewedChange={async (_id, reviewed) => {
          if (!reviewed) return;
          // Al marcar revisado, refrescamos la pagina, los KPIs y los bottlenecks
          // (la fila sale del universo evaluado).
          setRefreshKey((k) => k + 1);
        }}
      />
    </div>
  );
}

